import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'motion/react'
import { Feature } from '../../api/client'

interface ListEditorProps {
    feature: Feature
    values: Array<{
        id?: string
        value: string | number
    }>
    onChange: (values: Array<{ id?: string; value: string | number }>) => void
    onClose: () => void
    /** Choices mode: entries come from a fixed set instead of typed text —
     *  one add button per choice, each row shown by its choice's label.
     *  Used for list<bool> (the two answers, stored as 1 / 0). Repeats are
     *  always allowed here: a run of answers is the point. */
    choices?: Array<{ label: string; value: string | number }>
}

/**
 * Apple-style commit semantics: append, delete, and per-row text edits commit
 * upward via `onChange` immediately. Click-outside, Esc, and the header ×
 * dismiss without committing — the parent stamps `KEEP_VALUE` on every
 * commit, so dismiss-without-mutation leaves rows untouched.
 *
 * In-progress text edits commit on Enter or blur. Mid-keystroke dismissals
 * discard the typed value (matches the broader "dismiss never commits" rule;
 * recoverable by hitting Enter or tabbing out before dismissing).
 */
export const ListEditor: React.FC<ListEditorProps> = ({
    feature,
    values,
    onChange,
    onClose,
    choices,
}) => {
    const [items, setItems] = useState(
        values.map((v, i) => ({ ...v, id: v.id || `item-${i}` }))
    )
    const [newItemValue, setNewItemValue] = useState('')
    const [editingId, setEditingId] = useState<string | null>(null)
    const [dupWarning, setDupWarning] = useState(false)

    // ENFORCE_AS_SET_PLAN Stage 2: when the feature disallows repetitions,
    // reject committing a value already present. Identity is the stored value
    // itself — exact string for `str`, numeric equality for `num` (parseFloat
    // collapses 1.0/1.00), mirroring `_row_value_signature`.
    const enforceSet = feature.config?.enforce_as_set === true
    const listType = feature.config?.params?.type
    const isNumeric = listType === 'num'
    const containerRef = useRef<HTMLDivElement>(null)
    const itemsRef = useRef(items)
    useEffect(() => {
        itemsRef.current = items
    }, [items])
    // Last committed snapshot, used to snap an edited row back when its new
    // value would collide with another row.
    const committedRef = useRef(items)

    const warnTimerRef = useRef<ReturnType<typeof setTimeout>>()
    const flashDuplicate = () => {
        setDupWarning(true)
        if (warnTimerRef.current) clearTimeout(warnTimerRef.current)
        warnTimerRef.current = setTimeout(() => setDupWarning(false), 2500)
    }
    useEffect(
        () => () => {
            if (warnTimerRef.current) clearTimeout(warnTimerRef.current)
        },
        [],
    )

    const isDuplicateValue = (value: string | number, excludeId?: string) =>
        itemsRef.current.some((it) => it.id !== excludeId && it.value === value)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }

    const commit = (next: typeof items) => {
        setItems(next)
        committedRef.current = next
        onChange(next.map(({ id, value }) => ({ id, value })))
    }

    // Local-only: edit the value while the user types. The actual upward
    // commit happens on blur/Enter via `commitEditing`.
    const updateItem = (id: string, newValue: string | number) => {
        setItems((current) =>
            current.map((item) => (item.id === id ? { ...item, value: newValue } : item))
        )
    }

    const commitEditing = () => {
        const edited = itemsRef.current.find((item) => item.id === editingId)
        if (enforceSet && edited && isDuplicateValue(edited.value, editingId)) {
            // The edit would collide with another row; never commit it. Snap
            // the row back to the last committed snapshot and flash.
            setItems(committedRef.current)
            flashDuplicate()
            setEditingId(null)
            return
        }
        commit(itemsRef.current)
        setEditingId(null)
    }

    const removeItem = (id: string) => {
        commit(itemsRef.current.filter((item) => item.id !== id))
    }

    const addItem = () => {
        if (newItemValue.trim() === '') return
        const value = isNumeric ? parseFloat(newItemValue) : newItemValue
        if (isNumeric && isNaN(value as number)) return
        if (enforceSet && isDuplicateValue(value)) {
            flashDuplicate()
            return
        }
        const newId = `item-${Date.now()}`
        commit([...itemsRef.current, { id: newId, value }])
        setNewItemValue('')
    }

    const addChoice = (value: string | number) => {
        commit([...itemsRef.current, { id: `item-${Date.now()}-${itemsRef.current.length}`, value }])
    }

    const choiceLabel = (value: string | number) =>
        choices?.find((c) => c.value === value)?.label ?? String(value)

    const handleItemKeyDown = (e: React.KeyboardEvent, id: string) => {
        if (e.key === 'Enter') {
            commitEditing()
        } else if (e.key === 'Backspace') {
            const target = e.target as HTMLInputElement
            if (target.value === '') {
                e.preventDefault()
                removeItem(id)
                setEditingId(null)
            }
        }
    }

    return (
        <div
            ref={containerRef}
            className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-64 p-3"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">
                    Edit {feature.title}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded"
                    title="Dismiss"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="space-y-2 mb-3">
                {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                        {choices ? (
                            <div className="flex-1 px-2 py-1 text-sm">
                                {choiceLabel(item.value)}
                            </div>
                        ) : editingId === item.id ? (
                            <input
                                type={isNumeric ? 'number' : 'text'}
                                value={item.value}
                                onChange={(e) =>
                                    updateItem(
                                        item.id!,
                                        isNumeric ? parseFloat(e.target.value) || 0 : e.target.value
                                    )
                                }
                                onBlur={commitEditing}
                                onKeyDown={(e) => handleItemKeyDown(e, item.id!)}
                                className="flex-1 px-2 py-1 text-sm border border-amber-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
                                autoFocus
                            />
                        ) : (
                            <div
                                onClick={() => setEditingId(item.id!)}
                                className="flex-1 px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded"
                            >
                                {item.value}
                                {isNumeric && feature.unit ? feature.unit : ''}
                            </div>
                        )}

                        <button
                            onClick={() => removeItem(item.id!)}
                            className="p-1 text-gray-400 hover:text-rose-500 rounded"
                            title="Remove item"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                ))}

                {items.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-2">
                        No items yet
                    </div>
                )}
            </div>

            {choices ? (
                <div className="flex gap-2">
                    {choices.map((choice) => (
                        <motion.button
                            key={String(choice.value)}
                            type="button"
                            onClick={() => addChoice(choice.value)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                            className="flex-1 px-3 py-1 text-sm bg-action text-plum rounded hover:bg-action-dark"
                        >
                            + {choice.label}
                        </motion.button>
                    ))}
                </div>
            ) : (
            <div className="flex gap-2">
                <input
                    type={isNumeric ? 'number' : 'text'}
                    value={newItemValue}
                    onChange={(e) => setNewItemValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            addItem()
                        }
                    }}
                    placeholder={`Add ${isNumeric ? 'number' : 'text'}...`}
                    className={`flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                        dupWarning
                            ? 'border-rose-400 ring-1 ring-rose-300 focus:ring-rose-400'
                            : 'border-gray-300 focus:ring-action'
                    }`}
                />
                <button
                    onClick={addItem}
                    className="px-3 py-1 text-sm bg-action text-plum rounded hover:bg-action-dark"
                >
                    Add
                </button>
            </div>
            )}

            {dupWarning && (
                <div className="mt-2 text-xs text-rose-600" role="alert">
                    Already added
                </div>
            )}
        </div>
    )
}
